import React, { useRef } from 'react';

import LinkIcon from '@mui/icons-material/Link';
import {
  CircularProgress,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Box } from '@mui/system';

import { IIPENNotarySigner } from '../../../types/Notary';
import { IOrder } from '../../../types/Order';
import { successGreen, warningRed } from '../../constants/colors';
import { IIPENSessionState } from '../../redux-store/ipenSession/ipenSessionSlice';
import {
  formatDate,
  getWindowDimensions,
  phoneObjToString,
} from '../../utils/helpers';

export interface IProps {
  data?: IOrder;
  tableStyle?: Record<string, unknown>;
  ipenSession?: IIPENSessionState;
  notaryStatusOnOrder: string;
  getSsoToken: (arg: string) => any;
}

function ConformXNotaryDetails(props: IProps) {
  const { data, tableStyle, ipenSession, notaryStatusOnOrder, getSsoToken } =
    props;

  const form: any = useRef(null);
  const input: any = useRef(null);
  const notaryData: IIPENNotarySigner = { ...data?.OrderDetails?.IPEN?.Notary };
  const [isFetching, setIsFetching] = React.useState<boolean>(false);
  const windowSize = getWindowDimensions().width;
  const ipadView = windowSize > 1200 ? false : true;

  const signerEvents = (): boolean | undefined =>
    notaryData?.SolexSigner?.SignerEvents &&
    notaryData?.SolexSigner?.SignerEvents.length > 0;
  const notarySigned = (): boolean | undefined =>
    signerEvents() &&
    notaryData.SolexSigner?.SignerEvents.find((el) =>
      el.Name?.toLowerCase().includes('sign')
    ) != null;

  const openPartyLink = (): string | undefined => {
    if (
      signerEvents() &&
      notaryData.SolexSigner?.SignerEvents.find((el) =>
        el.Name?.toLowerCase().includes('links sent')
      )
    ) {
      const linkIndex = notaryData.SolexSigner?.SignerEvents.findIndex((el) =>
        el.Name?.toLowerCase().includes('links sent')
      );
      const partyLink: any =
        notaryData.SolexSigner?.SignerEvents[linkIndex].DocLink !== null
          ? notaryData.SolexSigner?.SignerEvents[linkIndex].DocLink
          : undefined;
      return partyLink;
    }
  };

  const execute = async (e: React.FormEvent) => {
    setIsFetching(true);
    e.preventDefault();
    const partyLink = openPartyLink();
    if (partyLink) {
      getSsoToken(partyLink).then((response: any) => {
        if (response && response.payload.length > 0) {
          if (input && form) {
            input.current.value = response.payload;
            form.current.submit();
          }
          setIsFetching(false);
        }
      });
    }
  };
  // link should be enable if: session is active and status is 'open, all signed' or open, link sent'
  const isLinkDisabled = (): boolean | undefined => {
    if (
      ipenSession?.isActiveSession &&
      (data?.Status?.toLowerCase().includes('open, links sent') ||
        data?.Status?.toLowerCase().includes('open, all signed'))
    ) {
      return false;
    }
    return true;
  };

  return (
    <>
      {ipadView ? (
        <Grid container sx={{ padding: 1 }}>
          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={1.5}>
                <div className='order-label'>FIRST</div>
                <div className='order-value'>
                  {notaryData?.FirstName || '-'}
                </div>
              </Grid>
              <Grid item xs={2.5}>
                <div className='order-label'>LAST</div>
                <div className='order-value'>{notaryData?.LastName || '-'}</div>
              </Grid>
              <Grid item xs={3.5}>
                <div className='order-label'>EMAIL</div>
                <div className='order-value'>{notaryData?.Email || '-'}</div>
              </Grid>
              <Grid item xs={2.5}>
                <div className='order-label'>PHONE NUMBER</div>
                <div className='order-value'>
                  {notaryData?.PhoneNumber
                    ? phoneObjToString(notaryData.PhoneNumber)
                    : ''}
                </div>
              </Grid>
              <Grid item xs={2}>
                <div className='order-label'>STATE</div>
                <div className='order-value'>
                  {notaryData?.CommissionState || '-'}
                </div>
              </Grid>
            </Grid>
            <Grid container sx={{ paddingTop: 2 }}>
              <Grid item xs={1.5}>
                <div className='order-label'>COUNTY</div>
                <div className='order-value'>
                  {notaryData?.CommissionCounty || '-'}
                </div>
              </Grid>
              <Grid item xs={2.5}>
                <div className='order-label'>COMMISSION NUMBER</div>
                <div className='order-value'>
                  {notaryData?.CommissionNumber || '-'}
                </div>
              </Grid>
              <Grid item xs={3.5}>
                <div className='order-label'>COMMISSION EXPIRATION DATE</div>
                <div className='order-value'>
                  {formatDate(
                    notaryData?.CommissionExpirationDate,
                    'ddd, MMM DD, YYYY'
                  ) || '-'}
                </div>
              </Grid>
              <Grid item xs={2.5}>
                <div className='order-label'>SIGN</div>
                <div className='order-value'>
                  {notarySigned() ? 'COMPLETE' : 'INCOMPLETE'}
                </div>
              </Grid>
              <Grid item xs={2}>
                <div className='order-label'>LINK</div>
                <div className='order-value'>
                  <form
                    ref={form}
                    target='_blank'
                    method='POST'
                    action={openPartyLink()}
                    onSubmit={(e: React.FormEvent) => execute(e)}
                    id='form'>
                    <input
                      ref={input}
                      type='hidden'
                      name='access_token'
                      value=''
                    />
                    {isFetching ? (
                      <Box sx={{ padding: '5px 7px' }}>
                        <CircularProgress color='inherit' size={20} />
                      </Box>
                    ) : (
                      <IconButton
                        aria-label='notary signing page'
                        size='small'
                        disabled={isLinkDisabled()}
                        data-testid='notary-link'
                        type='submit'>
                        <LinkIcon />
                      </IconButton>
                    )}
                  </form>
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <TableContainer>
          <Table style={tableStyle} aria-label='simple table' size='small'>
            <caption
              style={{
                textAlign: 'center',
                display: !notaryData.Email ? 'table-caption' : 'none',
              }}>
              {!notaryData.Email ? 'Notary not assigned' : ''}
            </caption>
            <TableHead>
              <TableRow>
                <TableCell>First</TableCell>
                <TableCell>Last</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Profile Status</TableCell>
                <TableCell>State</TableCell>
                <TableCell>County</TableCell>
                <TableCell>Commission Number</TableCell>
                <TableCell>Commission Expiration Date</TableCell>
                <TableCell>Sign</TableCell>
                <TableCell align='center'>Link</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notaryData.Email && (
                <TableRow
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell align='left'>
                    {notaryData?.FirstName || '-'}
                  </TableCell>
                  <TableCell align='left'>
                    {notaryData?.LastName || '-'}
                  </TableCell>
                  <TableCell align='left'>{notaryData?.Email || '-'}</TableCell>
                  <TableCell>
                    {notaryData?.PhoneNumber
                      ? phoneObjToString(notaryData.PhoneNumber)
                      : ''}
                  </TableCell>
                  <TableCell>{notaryStatusOnOrder}</TableCell>
                  <TableCell>{notaryData?.CommissionState || '-'}</TableCell>
                  <TableCell>{notaryData?.CommissionCounty || '-'}</TableCell>
                  <TableCell>{notaryData?.CommissionNumber || '-'}</TableCell>
                  <TableCell>
                    {formatDate(
                      notaryData?.CommissionExpirationDate,
                      'ddd, MMM DD, YYYY'
                    ) || '-'}
                  </TableCell>
                  <TableCell
                    style={{
                      color: notarySigned() ? successGreen : warningRed,
                    }}>
                    {notarySigned() ? 'Complete' : 'Incomplete'}
                  </TableCell>
                  <TableCell align='center'>
                    <form
                      ref={form}
                      target='_blank'
                      method='POST'
                      action={openPartyLink()}
                      onSubmit={(e: React.FormEvent) => execute(e)}
                      id='form'>
                      <input
                        ref={input}
                        type='hidden'
                        name='access_token'
                        value=''
                      />
                      {isFetching ? (
                        <Box sx={{ padding: '5px 7px' }}>
                          <CircularProgress color='inherit' size={20} />
                        </Box>
                      ) : (
                        <IconButton
                          aria-label='notary signing page'
                          size='small'
                          disabled={isLinkDisabled()}
                          data-testid='notary-link'
                          type='submit'>
                          <LinkIcon />
                        </IconButton>
                      )}
                    </form>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}

export default ConformXNotaryDetails;
